import type { RegisteredComponent } from "@builder.io/sdk-react";

import { AccordionComponent } from "@/components/Accordion/Accordion.builder";
import { AccordionItemComponent } from "@/components/AccordionItem/AccordionItem.builder";
import { AccountDashboardBannerComponent } from "@/components/AccountDashboardBanner";
import { AdditionalBenefitsComponent } from "@/components/AdditionalBenefits";
import { AnchorLinkComponent } from "@/components/AnchorLink/AnchorLink.builder";
import { AnchorPushComponent } from "@/components/AnchorPush";
import { ArticleRestrictedContentComponent } from "@/components/ArticleRestrictedContent";
import { BlogComponent } from "@/components/Blog/Blog.builder";
import { BlogArticleComponent } from "@/components/BlogArticle/BlogArticle.builder";
import { BlogArticleCarouselComponent } from "@/components/BlogArticleCarousel/BlogArticleCarousel.builder";
import { CallToActionComponent } from "@/components/CallToAction";
import { CheckoutComponent } from "@/components/Checkout";
import { ColumnComponent } from "@/components/Column/Column.builder";
import { ContactBlockComponent } from "@/components/ContactBlock/ContactBlock.builder";
import { ContactFormComponent } from "@/components/ContactForm/ContactForm.builder";
import { ContainerComponent } from "@/components/Container/Container.builder";
import { ContentGateComponent } from "@/components/ContentGate";
import { CtaComponent } from "@/components/Cta/Cta.builder";
import { CtaBlockComponent } from "@/components/CtaBlock/CtaBlock.builder";
import { EarnPointsComponent } from "@/components/EarnPoints/EarnPoints.builder";
import { EcommerceReassuranceBannerComponent } from "@/components/EcommerceReassuranceBanner/EcommerceReassuranceBanner.builder";
import { FaqArticleComponent } from "@/components/FaqArticle/FaqArticle.builder";
import { FaqResultsContainerComponent } from "@/components/FaqResultsContainer/FaqResultsContainer.builder";
import { FlavorFinderComponent } from "@/components/FlavorFinder/FlavorFinder.builder";
import { FlavourCarouselComponent } from "@/components/FlavourCarousel/FlavourCarousel.builder";
import { FlavourCarouselCardComponent } from "@/components/FlavourCarouselCard/FlavourCarouselCard.builder";
import { FooterListComponent } from "@/components/Footer/components/FooterList/FooterList.builder";
import { HealthBannerComponent } from "@/components/Footer/components/HealthBanner/HealthBanner.builder";
import { FormCheckboxComponent } from "@/components/FormCheckbox/FormCheckbox.builder";
import { FormConsentCheckboxListComponent } from "@/components/FormConsentCheckboxList/FormConsentCheckboxList.builder";
import { FormSelectComponent } from "@/components/FormSelect/FormSelect.builder";
import { FormTextAreaComponent } from "@/components/FormTextArea/FormTextArea.builder";
import { FormTextInputComponent } from "@/components/FormTextInput/FormTextInput.builder";
import { GridComponent } from "@/components/Grid/Grid.builder";
import { HeaderBlockComponent } from "@/components/HeaderBlock";
import { HeroBannerContainerComponent } from "@/components/HeroBannerContainer/HeroBannerContainer.builder";
import { HeroBannerWithCardsComponent } from "@/components/HeroBannerWithCards/HeroBannerWithCards.builder";
import { HeroContainerComponent } from "@/components/HeroContainer/HeroContainer.builder";
import { HeroComponent } from "@/components/HeroV2/HeroV2.builder";
import { HighlightCardComponent } from "@/components/HighlightCard/HighlightCard.builder";
import { ImageTopicGalleryComponent } from "@/components/ImageGallery/ImageGallery.builder";
import { ImageTopicGalleryTopicComponent } from "@/components/ImageGalleryTopic/ImageGalleryTopic.builder";
import { ImageInfoBannerComponent } from "@/components/ImageInfoBanner";
import { InfoBanner } from "@/components/InfoBanner/InfoBanner.builder";
import { LinksListComponent } from "@/components/LinksList/LinksList.builder";
import { LogoComponent } from "@/components/Logo";
import { LoyaltyTabsComponent } from "@/components/LoyaltyTabs";
import { LoyaltyWrapperComponent } from "@/components/LoyaltyWrapper";
import { MiscellaneousPageContainerComponent } from "@/components/MiscellaneousPageContainer/MiscellaneousPageContainer.builder";
import { NotFoundComponent } from "@/components/NotFound";
import { OverviewComponent } from "@/components/Overview/Overview.builder";
import { PackCodesComponent } from "@/components/PackCodes";
import { PointsAmountBannerComponent } from "@/components/PointsAmountBanner";
import { PointsHistoryComponent } from "@/components/PointsHistory";
import { ProductCardComponent } from "@/components/ProductCard/ProductCard.builder";
import { ProductCardContainerComponent } from "@/components/ProductCardContainer/ProductCardContainer.builder";
import { PushBlockComponent } from "@/components/PushBlock/PushBlock.builder";
import { PushBlockContainerComponent } from "@/components/PushBlockContainer/PushBlockContainer.builder";
import { QrAgeVerificationContainerComponent } from "@/components/QrAgeVerificationContainer/QrAgeVerificationContainer.builder";
import { QrCodeLotteryComponent } from "@/components/QrCodeLottery";
import { QrCodePointsCollectionComponent } from "@/components/QrCodePointsCollection";
import { QuickLinksComponent } from "@/components/QuickLinks/QuickLinks.builder";
import { ReassuranceBannerComponent } from "@/components/ReassuranceBanner";
import { ReferAFriendComponent } from "@/components/ReferAFriend";
import { ReferralHistoryComponent } from "@/components/ReferralHistory";
import { ReferralPersonalCodeComponent } from "@/components/ReferralPersonalCode";
import { ReferralStatsComponent } from "@/components/ReferralStats";
import { RowComponent } from "@/components/Row/Row.builder";
import { SearchBoxComponent } from "@/components/SearchBox";
import { SearchResultsComponent } from "@/components/SearchResults";
import { ImageNavLinksListComponent } from "@/components/SecondaryNavigation/components/ImageNavLinksList";
import { InnerContainerComponent } from "@/components/SecondaryNavigation/components/InnerContainer/InnerContainer.builder";
import { ListComponent } from "@/components/SecondaryNavigation/components/List/List.builder";
import { ShopTeaserComponent } from "@/components/SecondaryNavigation/components/ShopTeaser/ShopTeaser.builder";
import { SimpleProductTeaserComponent } from "@/components/SecondaryNavigation/components/SimpleProductTeaser/SimpleProductTeaser.builder";
import { SubmenuBottomActionComponent } from "@/components/SecondaryNavigation/components/SubmenuBottomAction/SubmenuBottomAction.builder";
import { SubmenuTitleComponent } from "@/components/SecondaryNavigation/components/SubmenuTitle/SubmenuTitle.builder";
import { SecondaryNavigationComponent } from "@/components/SecondaryNavigation/SecondaryNavigation.builder";
import { SectionComponent } from "@/components/Section/Section.builder";
import { SimplifiedPrizesComponent } from "@/components/SimplifiedPrizes";
import { SocialSharingIconsComponent } from "@/components/SocialSharingIcons/SocialSharingIcons.builder";
import { SoftAgeVerificationComponent } from "@/components/SoftAgeVerification";
import { StickyContainerComponent } from "@/components/StickyContainer/StickyContainer.builder";
import { StoreDetailsComponent } from "@/components/StoreDetails";
import { StoreLocatorComponent } from "@/components/StoreLocator";
import { TableComponent } from "@/components/Table/Table.builder";
import { TelephoneComponent } from "@/components/Telephone/Telephone.builder";
import { TiersBenefitsComponent } from "@/components/TiersBenefits";
import { TileComponent } from "@/components/Tile";
import { TrialHeroBannerComponent } from "@/components/TrialHeroBanner/TrialHeroBanner.builder";
import { TrustedPlacesComponent } from "@/components/TrustedPlaces";
import { TrustPilotMicroComboComponent } from "@/components/TrustPilotMicroCombo";
import { UserAuthComponent } from "@/components/UserAuth/UserAuth.builder";
import { UserProfileComponent } from "@/components/UserProfile/UserProfile.builder";
import { VideoContainerComponent } from "@/components/VideoContainer/VideoContainer.builder";
import { VideoPlayerComponent } from "@/components/VideoPlayer";
import { WelcomePopupComponent } from "@/components/WelcomePopup";

export const commonCustomComponents: RegisteredComponent[] = [
  AccordionComponent,
  AccordionItemComponent,
  AccountDashboardBannerComponent,
  AdditionalBenefitsComponent,
  AnchorLinkComponent,
  AnchorPushComponent,
  ArticleRestrictedContentComponent,
  BlogArticleCarouselComponent,
  BlogArticleComponent,
  BlogComponent,
  CallToActionComponent,
  CheckoutComponent,
  ColumnComponent,
  ContactBlockComponent,
  ContactFormComponent,
  ContainerComponent,
  CtaComponent,
  CtaBlockComponent,
  EarnPointsComponent,
  EcommerceReassuranceBannerComponent,
  FaqArticleComponent,
  FlavourCarouselCardComponent,
  FlavourCarouselComponent,
  FaqResultsContainerComponent,
  FlavorFinderComponent,
  FormCheckboxComponent,
  FormConsentCheckboxListComponent,
  FormSelectComponent,
  FormTextAreaComponent,
  FormTextInputComponent,
  HeaderBlockComponent,
  HeroComponent,
  HighlightCardComponent,
  ImageInfoBannerComponent,
  ImageTopicGalleryComponent,
  ImageTopicGalleryTopicComponent,
  InfoBanner,
  InnerContainerComponent,
  ProductCardContainerComponent,
  LinksListComponent,
  ListComponent,
  LogoComponent,
  LoyaltyTabsComponent,
  LoyaltyWrapperComponent,
  MiscellaneousPageContainerComponent,
  NotFoundComponent,
  OverviewComponent,
  PackCodesComponent,
  PointsAmountBannerComponent,
  PointsHistoryComponent,
  PushBlockComponent,
  PushBlockContainerComponent,
  QrCodeLotteryComponent,
  QrCodePointsCollectionComponent,
  QuickLinksComponent,
  ReassuranceBannerComponent,
  ReferAFriendComponent,
  ReferralHistoryComponent,
  ReferralPersonalCodeComponent,
  ReferralStatsComponent,
  RowComponent,
  SearchBoxComponent,
  SearchResultsComponent,
  SecondaryNavigationComponent,
  SectionComponent,
  ShopTeaserComponent,
  SimpleProductTeaserComponent,
  SimplifiedPrizesComponent,
  SocialSharingIconsComponent,
  SoftAgeVerificationComponent,
  StoreDetailsComponent,
  StoreLocatorComponent,
  SubmenuBottomActionComponent,
  SubmenuTitleComponent,
  TableComponent,
  TelephoneComponent,
  TiersBenefitsComponent,
  TileComponent,
  TrustedPlacesComponent,
  TrustPilotMicroComboComponent,
  UserAuthComponent,
  UserProfileComponent,
  VideoPlayerComponent,
  WelcomePopupComponent,
  SearchBoxComponent,
  CheckoutComponent,
  ImageTopicGalleryComponent,
  ImageTopicGalleryTopicComponent,
  TrustedPlacesComponent,
  HighlightCardComponent,
  ProductCardComponent,
  ContentGateComponent,
  ImageNavLinksListComponent,
  HealthBannerComponent,
  HeroBannerContainerComponent,
  HeroContainerComponent,
  VideoContainerComponent,
  FooterListComponent,
  QrAgeVerificationContainerComponent,
  HeroBannerWithCardsComponent,
  StickyContainerComponent,
  GridComponent,
  TrialHeroBannerComponent,
];
